import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

// The "here is exactly what you are about to be charged" block that sits
// directly above the Stripe card form.
//
// Deliberately presentational and generic (eyebrow / lines / total / note)
// rather than guest-specific: the member review step shows the same shape
// of information, so this stays reusable instead of becoming a second
// summary widget. Callers map their own summary object onto these props.
//
// Added for the guest seat checkout (2026-09-07), where the payment step
// showed a bare Stripe form with no indication of the amount — the total
// was visible on the seat form above, but scrolled well out of view by the
// time someone reached the card fields.
export default function CheckoutSummary({ eyebrow, lines = [], total, totalLabel = 'Total', note }) {
  const visibleLines = lines.filter(Boolean);
  if (!total && visibleLines.length === 0) return null;

  return (
    <Box
      sx={{
        mb: 3,
        p: { xs: 2.5, md: 3 },
        border: '1px solid rgba(36,34,88,0.15)',
        borderLeft: '3px solid var(--teal-500)',
        background: 'var(--cream-50)',
      }}
    >
      {eyebrow && (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--teal-500)',
            mb: 1.5,
          }}
        >
          {eyebrow}
        </Typography>
      )}

      <Stack spacing={0.75}>
        {visibleLines.map((line, i) => (
          <Typography
            key={i}
            sx={{
              fontFamily: 'var(--font-body)',
              fontSize: { xs: 14.5, md: 15 },
              color: 'var(--text-muted)',
              lineHeight: 1.5,
            }}
          >
            {line}
          </Typography>
        ))}
      </Stack>

      {total && (
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(36,34,88,0.15)', gap: 2 }}
        >
          <Typography
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--navy-500)',
            }}
          >
            {totalLabel}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'var(--font-display)',
              fontSize: { xs: 26, md: 30 },
              fontWeight: 800,
              lineHeight: 1.1,
              color: 'var(--navy-500)',
            }}
          >
            {total}
          </Typography>
        </Stack>
      )}

      {note && (
        <Typography
          sx={{
            mt: 1.25,
            fontFamily: 'var(--font-body)',
            fontSize: 13.5,
            lineHeight: 1.5,
            color: 'var(--text-muted)',
          }}
        >
          {note}
        </Typography>
      )}
    </Box>
  );
}
